/**
 * User symbols: drawn in the symbol editor from a few shapes on a 1.25 mm grid, stored in the
 * project as ordinary symbol definitions (paths + ports), ids "user.…".
 */
import type { CustomSymbol } from '@elec/domain';
import type { PortDirection } from './types.ts';

export type Shape =
  | { t: 'line'; x1: number; y1: number; x2: number; y2: number }
  | { t: 'rect'; x: number; y: number; w: number; h: number }
  | { t: 'circle'; cx: number; cy: number; r: number; fill?: boolean }
  | { t: 'text'; x: number; y: number; text: string; size: number };

export interface SymbolDraft {
  id?: string;
  name: { fr: string; en: string; nl: string };
  tagPrefix: string;
  views: string[];
  width: number;
  height: number;
  shapes: Shape[];
  ports: Array<{ id: string; x: number; y: number; dir: PortDirection }>;
}

const n = (v: number) => String(Math.round(v * 1000) / 1000);
const K = 0.5523; // cubic Bézier approximation of a quarter circle

export function shapePath(s: Shape): string | undefined {
  switch (s.t) {
    case 'line':
      return `M${n(s.x1)} ${n(s.y1)}L${n(s.x2)} ${n(s.y2)}`;
    case 'rect':
      return `M${n(s.x)} ${n(s.y)}L${n(s.x + s.w)} ${n(s.y)}L${n(s.x + s.w)} ${n(s.y + s.h)}L${n(s.x)} ${n(s.y + s.h)}Z`;
    case 'circle': {
      const { cx, cy, r } = s;
      const k = K * r;
      return (
        `M${n(cx + r)} ${n(cy)}` +
        `C${n(cx + r)} ${n(cy + k)} ${n(cx + k)} ${n(cy + r)} ${n(cx)} ${n(cy + r)}` +
        `C${n(cx - k)} ${n(cy + r)} ${n(cx - r)} ${n(cy + k)} ${n(cx - r)} ${n(cy)}` +
        `C${n(cx - r)} ${n(cy - k)} ${n(cx - k)} ${n(cy - r)} ${n(cx)} ${n(cy - r)}` +
        `C${n(cx + k)} ${n(cy - r)} ${n(cx + r)} ${n(cy - k)} ${n(cx + r)} ${n(cy)}Z`
      );
    }
    case 'text':
      return undefined;
  }
}

/** Direction of a port from where it sits on the symbol outline. */
export function portDirFor(x: number, y: number, width: number, height: number): PortDirection {
  const d = [
    ['up', y],
    ['down', height - y],
    ['left', x],
    ['right', width - x],
  ] as const;
  return [...d].sort((a, b) => a[1] - b[1])[0]![0];
}

export function draftToSymbol(d: SymbolDraft, newId: () => string): CustomSymbol & { shapes: Shape[] } {
  const paths: string[] = [];
  const fills: string[] = [];
  const texts: NonNullable<CustomSymbol['texts']> = [];
  for (const s of d.shapes) {
    if (s.t === 'text') texts.push({ text: s.text, x: s.x, y: s.y, size: s.size });
    else {
      const p = shapePath(s)!;
      (s.t === 'circle' && s.fill ? fills : paths).push(p);
    }
  }
  return {
    id: d.id ?? `user.${newId()}`,
    name: d.name,
    category: 'user',
    views: d.views,
    width: d.width,
    height: d.height,
    paths,
    ...(fills.length ? { fills } : {}),
    ...(texts.length ? { texts } : {}),
    ports: d.ports,
    tagPrefix: d.tagPrefix || 'X',
    label: ['{description}'],
    shapes: d.shapes,
  };
}

/** Problems that would make the symbol unusable. */
export function draftProblems(d: SymbolDraft): string[] {
  const out: string[] = [];
  if (!d.name.fr.trim() && !d.name.en.trim() && !d.name.nl.trim()) out.push('name');
  if (!d.shapes.length) out.push('empty');
  if (!d.views.length) out.push('views');
  const ids = new Set<string>();
  for (const p of d.ports) {
    if (ids.has(p.id)) out.push(`port:${p.id}`);
    ids.add(p.id);
    if (p.x < 0 || p.y < 0 || p.x > d.width || p.y > d.height) out.push(`port-outside:${p.id}`);
  }
  return out;
}

/** Library file of user symbols (to share between projects or colleagues). */
export interface SymbolLibraryFile {
  app: 'tablo' | 'elec';
  kind: 'symbols';
  symbols: CustomSymbol[];
}

export function isSymbolLibraryFile(v: unknown): v is SymbolLibraryFile {
  return (
    !!v &&
    typeof v === 'object' &&
    ['tablo', 'elec'].includes((v as SymbolLibraryFile).app) &&
    (v as SymbolLibraryFile).kind === 'symbols' &&
    Array.isArray((v as SymbolLibraryFile).symbols)
  );
}
